import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import Loader from './components/Loader/Loader';
import { ToastContainer } from './components/Toast/Toast';
import AlertToast from './components/AlertToast/AlertToast';
import NewFeatureToast from './components/NewFeatureToast/NewFeatureToast';
import InstallBanner from './components/InstallBanner/InstallBanner';
import Footer from './components/Footer/Footer';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import TradingPage from './pages/TradingPage';
import RsiHeatmapPage from './pages/RsiHeatmapPage';
import BubblesPage from './pages/BubblesPage';
import HyperliquidPositionsPage from './pages/HyperliquidPositionsPage';
import LiquidationMapPage from './pages/LiquidationMapPage';
import WalletProfilePage from './pages/WalletProfilePage';
import PolymarketPage from './pages/PolymarketPage';
import LeaderboardPage from './pages/LeaderboardPage';
import EconomicCalendarPage from './pages/EconomicCalendarPage';
import AltcoinIndexPage from './pages/AltcoinIndexPage';
import EtfFlowPage from './pages/EtfFlowPage';
import InvestmentCalculatorPage from './pages/InvestmentCalculatorPage';
import CoinPage from './pages/CoinPage';
import InsightsPage from './pages/InsightsPage';
import InsightDetailPage from './pages/InsightDetailPage';
import InsightOrCategoryPage from './pages/InsightOrCategoryPage';
import AiTraderPage from './pages/AiTraderPage';
import TradingPagePro from './pages/TradingPagePro';
import WidgetCustomizerPage from './pages/WidgetCustomizerPage';
import WidgetEmbedPage from './pages/WidgetEmbedPage';
import StatsPage from './pages/StatsPage';
import CoinTerminalPage from './pages/CoinTerminalPage';
import LandingPage from './pages/LandingPage';
import NotFoundPage from './pages/NotFoundPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfUsePage from './pages/TermsOfUsePage';
import DisclaimerPage from './pages/DisclaimerPage';
import { SoundNotificationsProvider } from './contexts/SoundNotificationsContext';
import { LanguageProvider, SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE } from './contexts/LanguageContext';

// Import i18n configuration
import './i18n/i18n';

// Import PostHog analytics
import { initAnalytics, trackPageView, setupFrustrationTracking, trackEntryPoint, startEngagementTracking } from './utils/analytics';
import { initWebVitals } from './utils/webVitals';
import { initInstallPromptCapture } from './utils/installPromptCapture';
import { trackPwaSession } from './utils/pwaSessionTracking';

// Feedback survey
import FeedbackSurvey from './components/FeedbackSurvey/FeedbackSurvey';
import useFeedbackSurvey from './hooks/useFeedbackSurvey';

// Component that renders routes for both default and language-prefixed paths
function AppRoutes() {
  return (
    <Routes>
      {/* Default routes (English - no prefix) */}
      <Route path="/trades" element={<TradingPage />} />
      <Route path="/terminal" element={<TradingPagePro />} />
      <Route path="/trades/pro" element={<Navigate to="/terminal" replace />} />
      <Route path="/rsi" element={<RsiHeatmapPage />} />
      <Route path="/bubbles" element={<BubblesPage />} />
      <Route path="/hyperliquid" element={<HyperliquidPositionsPage />} />
      <Route path="/hyperliquid/wallet/:address" element={<WalletProfilePage />} />
      <Route path="/liquidation-map" element={<LiquidationMapPage />} />
      <Route path="/polymarket" element={<PolymarketPage />} />
      <Route path="/leaderboard" element={<LeaderboardPage />} />
      <Route path="/economic-calendar" element={<EconomicCalendarPage />} />
      <Route path="/altcoin-index" element={<AltcoinIndexPage />} />
      <Route path="/etf-flows" element={<EtfFlowPage />} />
      <Route path="/calculator" element={<InvestmentCalculatorPage />} />
      <Route path="/calculator/:coinSymbol" element={<InvestmentCalculatorPage />} />
      <Route path="/price/:coinSymbol" element={<CoinPage />} />
      <Route path="/insights" element={<InsightsPage />} />
      <Route path="/insights/:slug" element={<InsightOrCategoryPage />} />
      <Route path="/ai-trader" element={<AiTraderPage />} />
      <Route path="/widgets" element={<WidgetCustomizerPage />} />
      <Route path="/widgets/btc-trades" element={<WidgetEmbedPage />} />
      <Route path="/chart" element={<CoinTerminalPage />} />
      <Route path="/chart/:coinSymbol" element={<ChartCoinSlugRedirect />} />
      <Route path="/stats/:coinSymbol" element={<StatsToChartRedirect />} />
      <Route path="/stats" element={<StatsPage />} />

      {/* Legal pages */}
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsOfUsePage />} />
      <Route path="/disclaimer" element={<DisclaimerPage />} />

      {/* Language-prefixed routes */}
      <Route path="/:lang/trades" element={<LocalizedRoute><TradingPage /></LocalizedRoute>} />
      <Route path="/:lang/terminal" element={<LocalizedRoute><TradingPagePro /></LocalizedRoute>} />
      <Route path="/:lang/rsi" element={<LocalizedRoute><RsiHeatmapPage /></LocalizedRoute>} />
      <Route path="/:lang/bubbles" element={<LocalizedRoute><BubblesPage /></LocalizedRoute>} />
      <Route path="/:lang/hyperliquid" element={<LocalizedRoute><HyperliquidPositionsPage /></LocalizedRoute>} />
      <Route path="/:lang/hyperliquid/wallet/:address" element={<LocalizedRoute><WalletProfilePage /></LocalizedRoute>} />
      <Route path="/:lang/liquidation-map" element={<LocalizedRoute><LiquidationMapPage /></LocalizedRoute>} />
      <Route path="/:lang/polymarket" element={<LocalizedRoute><PolymarketPage /></LocalizedRoute>} />
      <Route path="/:lang/leaderboard" element={<LocalizedRoute><LeaderboardPage /></LocalizedRoute>} />
      <Route path="/:lang/economic-calendar" element={<LocalizedRoute><EconomicCalendarPage /></LocalizedRoute>} />
      <Route path="/:lang/altcoin-index" element={<LocalizedRoute><AltcoinIndexPage /></LocalizedRoute>} />
      <Route path="/:lang/etf-flows" element={<LocalizedRoute><EtfFlowPage /></LocalizedRoute>} />
      <Route path="/:lang/calculator" element={<LocalizedRoute><InvestmentCalculatorPage /></LocalizedRoute>} />
      <Route path="/:lang/calculator/:coinSymbol" element={<LocalizedRoute><InvestmentCalculatorPage /></LocalizedRoute>} />
      <Route path="/:lang/price/:coinSymbol" element={<LocalizedRoute><CoinPage /></LocalizedRoute>} />
      <Route path="/:lang/insights" element={<LocalizedRoute><InsightsPage /></LocalizedRoute>} />
      <Route path="/:lang/insights/:slug" element={<LocalizedRoute><InsightOrCategoryPage /></LocalizedRoute>} />
      <Route path="/:lang/ai-trader" element={<LocalizedRoute><AiTraderPage /></LocalizedRoute>} />
      <Route path="/:lang/widgets" element={<LocalizedRoute><WidgetCustomizerPage /></LocalizedRoute>} />
      <Route path="/:lang/chart" element={<LocalizedRoute><CoinTerminalPage /></LocalizedRoute>} />
      <Route path="/:lang/chart/:coinSymbol" element={<ChartCoinSlugRedirect />} />
      <Route path="/:lang/stats/:coinSymbol" element={<LocalizedRoute><StatsToChartRedirect /></LocalizedRoute>} />
      <Route path="/:lang/stats" element={<LocalizedRoute><StatsPage /></LocalizedRoute>} />

      {/* Legal pages - language prefixed */}
      <Route path="/:lang/privacy" element={<LocalizedRoute><PrivacyPolicyPage /></LocalizedRoute>} />
      <Route path="/:lang/terms" element={<LocalizedRoute><TermsOfUsePage /></LocalizedRoute>} />
      <Route path="/:lang/disclaimer" element={<LocalizedRoute><DisclaimerPage /></LocalizedRoute>} />

      {/* Landing page */}
      <Route path="/" element={<LandingPage />} />

      {/* Catch language-only paths and redirect to trades */}
      <Route path="/:lang" element={<LanguageOnlyRedirect />} />

      {/* 404 - Catch all unmatched routes */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

// Wrapper component that validates language prefix
function LocalizedRoute({ children }) {
  // The language is validated and synced by LanguageContext
  // We just need to render the children here
  return children;
}

// Canonical chart route lost its coin slug — the cockpit page now lets
// each widget pick its own coin via the in-header picker. Legacy URLs
// like /chart/BTC or /:lang/chart/ETH redirect to the slug-less canonical
// /chart (preserving language prefix). External backlinks that pointed
// at a specific coin land on the cockpit; the user can pick from the
// per-widget coin selector once there.
function ChartCoinSlugRedirect() {
  const { lang } = useParams();
  const prefix = lang ? `/${lang}` : '';
  return <Navigate to={`${prefix}/chart`} replace />;
}

// Legacy /stats/:coin and /:lang/stats/:coin URLs go to canonical /chart.
function StatsToChartRedirect() {
  const { lang } = useParams();
  const prefix = lang ? `/${lang}` : '';
  return <Navigate to={`${prefix}/chart`} replace />;
}

// Redirect from /:lang to /:lang/trades
function LanguageOnlyRedirect() {
  const path = window.location.pathname;
  const lang = path.split('/').filter(Boolean)[0];

  if (SUPPORTED_LANGUAGES.includes(lang)) {
    return <Navigate to={`/${lang}/trades`} replace />;
  }

  // Invalid language code - redirect to default
  return <Navigate to="/trades" replace />;
}

function AppContent() {
  const location = useLocation();
  const { showSurvey, dismissSurvey } = useFeedbackSurvey();

  // Track entry point once per session (on any page, not just landing)
  useEffect(() => {
    const hasTrackedEntry = sessionStorage.getItem('crypnal_entry_tracked');
    if (!hasTrackedEntry) {
      trackEntryPoint();
      sessionStorage.setItem('crypnal_entry_tracked', 'true');
    }
  }, []);

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Track page views on route change for Google Analytics and PostHog
  useEffect(() => {
    // Google Analytics
    if (window.gtag) {
      window.gtag('config', 'G-LXEL6KHL5X', {
        page_path: location.pathname + location.search
      });
    }

    // PostHog - extract page name from path
    const pathParts = location.pathname.split('/').filter(Boolean);
    // Remove language prefix if present
    const pageName = SUPPORTED_LANGUAGES.includes(pathParts[0])
      ? pathParts[1] || 'trades'
      : pathParts[0] || 'trades';

    trackPageView(pageName, {
      full_path: location.pathname,
      search: location.search
    });

    // Start engagement tracking for this page (tracks active/idle/hidden time)
    startEngagementTracking(location.pathname);
  }, [location]);

  const isLandingPage = location.pathname === '/';
  const isAiTraderPage = location.pathname === '/ai-trader' || location.pathname.match(/^\/[a-z]{2}\/ai-trader$/);
  const isWidgetEmbed = location.pathname.startsWith('/widgets/');
  const isProPage = location.pathname.endsWith('/terminal');
  const isCoinTerminal = /\/(chart|stats)\/[A-Za-z0-9]+/.test(location.pathname);

  return (
    <LanguageProvider>
      {/* InstallBanner must be FIRST in DOM order: it uses position:sticky
         top:0, and a sticky element only sticks within its DOM position.
         If rendered after the page content, the banner sits below the
         fold on first paint and is invisible until the user scrolls past
         everything to reach it. */}
      {!isWidgetEmbed && !isLandingPage && <InstallBanner />}
      <AppRoutes />
      {!isLandingPage && !isAiTraderPage && !isWidgetEmbed && !isProPage && !isCoinTerminal && <Footer />}
      {!isWidgetEmbed && <FeedbackSurvey show={showSurvey} onDismiss={dismissSurvey} />}
      {!isWidgetEmbed && <AlertToast />}
      {!isWidgetEmbed && <NewFeatureToast />}
    </LanguageProvider>
  );
}

function App() {
  // Initialize PostHog analytics and tracking on app startup
  useEffect(() => {
    initAnalytics();
    // Skip PWA install capture + session tracking on widget embeds. Same
    // gate as SW registration in main.jsx: a /widgets/* iframe should not
    // increment tm.pwa.visitCount or emit install funnel events when the
    // banner itself is suppressed at the AppContent level.
    const isWidgetEmbed = typeof window !== 'undefined' &&
      window.location.pathname.startsWith('/widgets/');
    if (!isWidgetEmbed) {
      initInstallPromptCapture();
    }
    // Initialize web vitals and frustration tracking after a short delay
    // to ensure PostHog is fully loaded. The PWA session event also fires
    // inside that timeout so super-properties land in PostHog before any
    // event is dispatched (posthog.init() is async — register() before
    // `loaded` is silently dropped).
    const timer = setTimeout(() => {
      initWebVitals();
      setupFrustrationTracking();
      if (!isWidgetEmbed) {
        trackPwaSession({ searchParams: window.location.search });
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <ErrorBoundary>
      <SoundNotificationsProvider>
        <Loader />
        <ToastContainer />
        <Router>
          <AppContent />
        </Router>
      </SoundNotificationsProvider>
    </ErrorBoundary>
  );
}

export default App;
